import { Component, OnInit, signal, WritableSignal, ChangeDetectorRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { SecurityService } from '../../services/security.service';
import { LoaderService } from '../../services/loader.service';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import { ConfigInput, ConfigSelected } from '../../interfaces/interfaces';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../shared/input/input.component';
import { SelectedComponent } from '../../shared/selected/selected.component';
import { COUNTRIES } from '../../config/constants.config';
import { RouteService } from '../../services/route.service';
import { HelpService } from '../../services/help.service';
import { OtpSessionService } from '../../services/otp-session.service';
import { maxTodayColombiaValidator } from 'app/config/functions.shared';
import { toast } from 'ngx-sonner';
import { debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { ListsService } from 'app/services/lists.service';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface TinFormat {
  id: number;
  regular_expression: string;
  description?: string;
}

@Component({
  selector: 'app-foreign',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent,
  ],
  templateUrl: './foreign.component.html',
  styleUrl: './foreign.component.css'
})
export class ForeignComponent implements OnInit {
  form: FormGroup;
  inputCountryValue: string = '';
  filteredCountries: Country[] = [];
  isTimeout = false;
  countries: Country[] = [];
  selectedCountryFormats: TinFormat[] = [];
  isLoadingFormats = false;
  isLoadingCountries = false;
  nextRoute: WritableSignal<string> = signal('');
  private searchSubject = new Subject<string>();
  private selectedCountry: Country | null = null;
  /** Evita limpiar el TIN mientras se hidrata getForeign / país guardado */
  private isHydratingForeign = false;
  private pendingTin: string | null = null;
  /** app-selected emite el default false con debounce y puede pisar getForeign */
  private ignoreStaleSelectedUntil = 0;

  dataSelected: WritableSignal<ConfigSelected[]> = signal([
    { label: '¿Administras recursos públicos o ejerces un cargo importante?', subLabel: '', formName: 'hasPublicJobManager', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Eres familiar de un PEP? (cónyuge o compañero permanente, hijo, padre, hermano, nieto, abuelo, suegro o cuñado)', subLabel: '', formName: 'isPepRelative', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Tienes Green Card?', subLabel: '', formName: 'hasGreenCard', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Tus padres tienen nacionalidad norteamericana?', subLabel: '', formName: 'hasNothAmericanNationality', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Está obligado a tributar en un país diferente a Colombia?', subLabel: '', formName: 'isObligatedTaxOtherCountry', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Es extranjero residente?', subLabel: '', formName: 'isForeignResident', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Ha estado más de 183 días en el último año, o 183 días en los últimos 3 años, en Estados Unidos o fuera del país?', subLabel: '', formName: 'hasStayedAbroad183Days', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
  ]);

  pepDocumentTypeSelected: WritableSignal<ConfigSelected> = signal({
    label: 'Tipo de documento',
    subLabel: '',
    formName: 'pepRelativeDocumentType',
    placeholder: '',
    type: 'text',
    defaultValue: '',
    enabled: true,
    validation: [Validators.required],
    textError: 'Por favor selecciona un tipo de documento',
    options: signal([
      { value: 1, label: 'Cédula de ciudadanía' },
      { value: 2, label: 'Cédula de extranjería' },
      { value: 48, label: 'PPT' },
      { value: 5, label: 'Pasaporte' },
    ]),
  });

  publicJobInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Cargo', subLabel: '*', formName: 'publicJobPosition', placeholder: 'Ingresa el cargo', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required, Validators.minLength(3)], textError: 'El cargo debe tener mínimo 3 caracteres.' },
    { label: 'Fecha de ingreso al cargo', subLabel: '*', formName: 'publicJobStartDate', placeholder: '', type: 'date', defaultValue: '', enabled: true, validation: [Validators.required, maxTodayColombiaValidator], textError: 'Selecciona una fecha de ingreso válida.' },
    { label: 'Fecha de retiro del cargo (cuando aplique)', subLabel: '', formName: 'publicJobEndDate', placeholder: '', type: 'date', defaultValue: '', enabled: true, validation: [maxTodayColombiaValidator], textError: 'La fecha de retiro no puede ser futura.' },
  ]);

  pepRelativeInputs: WritableSignal<ConfigInput[]> = signal([
    { label: 'Nombres y apellidos', subLabel: '*', formName: 'pepRelativeFullName', placeholder: 'Ingresa nombres y apellidos', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required, Validators.minLength(7)], textError: 'Ingresa nombres y apellidos con mínimo 7 caracteres.' },
    { label: 'Número de documento', subLabel: '*', formName: 'pepRelativeDocumentNumber', placeholder: 'Ingresa el número de documento', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required, Validators.minLength(6), Validators.maxLength(10), Validators.pattern(/^[a-zA-Z0-9]+$/)], textError: 'Ingresa un número de documento alfanumérico de 6 a 10 caracteres.' },
    { label: 'Cargo / Entidad del PEP', subLabel: '*', formName: 'pepRelativePositionEntity', placeholder: 'Ingresa el cargo o entidad', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required, Validators.minLength(3)], textError: 'Ingresa el cargo o entidad del PEP con mínimo 3 caracteres.' },
  ]);

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _listService: ListsService,
    private _loaderService: LoaderService,
    private _routeService: RouteService,
    private _helpService: HelpService,
    private _otpSessionService: OtpSessionService,
    private _cdr: ChangeDetectorRef
  ) {
    this._title.setTitle('Onboarding Digital | Información Adicional');
    this.form = new FormGroup({
      hasPublicJobManager: new FormControl(null, [Validators.required]),
      isPepRelative: new FormControl(null, [Validators.required]),
      publicJobPosition: new FormControl(null),
      publicJobStartDate: new FormControl(null),
      publicJobEndDate: new FormControl(null),
      pepRelativeFullName: new FormControl(null),
      pepRelativeDocumentType: new FormControl(null),
      pepRelativeDocumentNumber: new FormControl(null),
      pepRelativePositionEntity: new FormControl(null),
      hasGreenCard: new FormControl(null, [Validators.required]),
      hasNothAmericanNationality: new FormControl(null, [Validators.required]),
      isObligatedTaxOtherCountry: new FormControl(null, [Validators.required]),
      obligatedTaxCountry: new FormControl(null),
      obligatedTaxCountryId: new FormControl(null),
      tinNumber: new FormControl(null),
      isForeignResident: new FormControl(null, [Validators.required]),
      hasStayedAbroad183Days: new FormControl(null, [Validators.required])
    }, [this.countryValidator.bind(this), this.tinValidator.bind(this)]);

    this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');

    // Suscripciones para campos condicionales
    this.form.get('isObligatedTaxOtherCountry')?.valueChanges.subscribe((value: boolean) => {
      this.toggleCountryValidation(value);
      this.toggleTinValidation(value);
    });

    this.form.get('obligatedTaxCountryId')?.valueChanges.subscribe((countryId: number) => {
      if (countryId) {
        this.loadCountryFormats(countryId);
      } else {
        this.selectedCountryFormats = [];
        this.selectedCountry = null;
        // No borrar el TIN durante la hidratación de getForeign
        if (!this.isHydratingForeign) {
          this.form.get('tinNumber')?.setValue('');
        }
        this._cdr.detectChanges();
      }
      this.updateTinValidation();
    });

    this.form.get('hasPublicJobManager')?.valueChanges.subscribe((value) => {
      this.togglePublicJobValidation(value);
    });

    this.form.get('isPepRelative')?.valueChanges.subscribe((value) => {
      this.togglePepRelativeValidation(value);
    });

    // Configuración del buscador de países
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        if (searchTerm && searchTerm.length >= 2) {
          this.isLoadingCountries = true;
          this._cdr.detectChanges();
          return this._listService.searchCountries(searchTerm).pipe(
            finalize(() => {
              this.isLoadingCountries = false;
              this._cdr.detectChanges();
            })
          );
        } else {
          this.filteredCountries = [];
          this.isLoadingCountries = false;
          this._cdr.detectChanges();
          return of({ countries: [] });
        }
      })
    ).subscribe({
      next: (response: any) => {
        this.filteredCountries = response.countries.map((c: any) => ({
          id: c.id,
          name: c.name,
          code: c.code
        }));
        this._cdr.detectChanges();
      },
      error: (error: any) => {
        console.error("ERROR buscando países: ", error);
        this.filteredCountries = [];
        this.isLoadingCountries = false;
        this._cdr.detectChanges();
        toast.error('Error al buscar países.');
      }
    });
  }

  async ngOnInit() {
    const authRoute = localStorage.getItem('route');
    if(!authRoute) {
      this._router.navigateByUrl('');
    }
    const currentUrl = this._router.url;
    if(authRoute && currentUrl !== authRoute) {
      await this._router.navigateByUrl(authRoute);
    } else {
      this._loaderService.hide();
      this._helpService.show();
      this.loadSavedCountry();
      this.getForeign();
      this.toggleCountryValidation(this.form.get('isObligatedTaxOtherCountry')?.value);
      this.toggleTinValidation(this.form.get('isObligatedTaxOtherCountry')?.value);
      this.togglePublicJobValidation(this.form.get('hasPublicJobManager')?.value);
      this.togglePepRelativeValidation(this.form.get('isPepRelative')?.value);
    }
  }

  get showPublicJobFields(): boolean {
    const value = this.form.get('hasPublicJobManager')?.value;
    return value === true || value === 'true';
  }

  get showPepRelativeFields(): boolean {
    const value = this.form.get('isPepRelative')?.value;
    return value === true || value === 'true';
  }

  loadSavedCountry() {
    const savedCountryId = this.form.get('obligatedTaxCountryId')?.value;
    if (savedCountryId) {
      this._listService.getCountryFormats(savedCountryId).subscribe({
        next: (response: any) => {
          this.loadCountriesList();
        },
        error: () => {
          this.loadCountriesList();
        }
      });
    } else {
      this.countries = [];
    }
  }

  loadCountriesList() {
    this.countries = [];
  }

  loadCountryFormats(countryId: number, onDone?: () => void) {
    if (!countryId) {
      this.selectedCountryFormats = [];
      this._cdr.detectChanges();
      onDone?.();
      return;
    }

    this.isLoadingFormats = true;
    this._cdr.detectChanges();

    this._listService.getCountryFormats(countryId)
      .pipe(
        finalize(() => {
          this.isLoadingFormats = false;
          this._cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.selectedCountryFormats = response.formats || [];
          // Reaplicar TIN guardado después de cargar formatos (pueden revalidar el control)
          if (this.isHydratingForeign) {
            this.applyPendingTin();
          }
          this.updateTinValidation();
          this._cdr.detectChanges();
          onDone?.();
        },
        error: (error: any) => {
          console.error("ERROR cargando formatos: ", error);
          this.selectedCountryFormats = [];
          if (!this.isHydratingForeign) {
            toast.error('No se pudieron cargar los formatos TIN del país seleccionado.');
          }
          this._cdr.detectChanges();
          onDone?.();
        }
      });
  }

  getForeign() {
    this._securityService.getForeign().subscribe({
      next: (response: any) => {
        this.isHydratingForeign = true;
        this.pendingTin = (response.tinNumber ?? response.tin ?? null)
          ? String(response.tinNumber ?? response.tin).trim()
          : null;

        // No incluir tin/tinNumber en patchValue: se aplica al final con applyPendingTin()
        const { tin, tinNumber, ...formPatch } = response;
        this.form.patchValue(formPatch);

        this.dataSelected.update((inputs: any) =>
          inputs.map((input: any) => ({
            ...input,
            defaultValue: response[input.formName] ?? input.defaultValue
          }))
        );

        this.publicJobInputs.update((inputs) =>
          inputs.map((input) => ({
            ...input,
            defaultValue: response[input.formName!] ?? input.defaultValue,
          }))
        );

        this.pepRelativeInputs.update((inputs) =>
          inputs.map((input) => ({
            ...input,
            defaultValue: response[input.formName!] ?? input.defaultValue,
          }))
        );

        this.pepDocumentTypeSelected.update((config) => ({
          ...config,
          defaultValue: response.pepRelativeDocumentType ?? config.defaultValue,
        }));

        this.toggleCountryValidation(response.isObligatedTaxOtherCountry);
        this.toggleTinValidation(response.isObligatedTaxOtherCountry);
        this.togglePublicJobValidation(response.hasPublicJobManager);
        this.togglePepRelativeValidation(response.isPepRelative);

        const countryId = response.obligatedTaxCountryId;
        const countryCode = response.obligatedTaxCountry;

        if (countryCode) {
          this.handleCountryByCode(countryCode, () => this.finishForeignHydration());
        } else if (countryId) {
          this.form.get('obligatedTaxCountryId')?.setValue(countryId);
          this.form.get('obligatedTaxCountry')?.setValue(countryCode);
          this.loadCountryFormats(countryId, () => this.finishForeignHydration());
        } else {
          this.inputCountryValue = '';
          this.finishForeignHydration();
        }
      },
      error: (error: any) => {
        this.isHydratingForeign = false;
        this.pendingTin = null;
        console.error("ERROR: ", error);
        toast.error('No se pudo cargar la información guardada.');
      }
    });
  }

  private finishForeignHydration(): void {
    this.applyPendingTin();
    this.isHydratingForeign = false;
    // Ventana para ignorar emits stale de app-selected (default false → debounce)
    this.ignoreStaleSelectedUntil = Date.now() + 2000;
    this.form.updateValueAndValidity();
    this._cdr.detectChanges();

    // Reaplicar una vez más por si un emit llega justo al cerrar la hidratación
    setTimeout(() => {
      this.applyPendingTin();
      this._cdr.detectChanges();
    }, 250);
  }

  private applyPendingTin(): void {
    if (!this.pendingTin) {
      return;
    }
    const control = this.form.get('tinNumber');
    if (control?.value !== this.pendingTin) {
      control?.setValue(this.pendingTin, { emitEvent: false });
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private isIgnoringStaleSelected(): boolean {
    return this.isHydratingForeign || Date.now() < this.ignoreStaleSelectedUntil;
  }

  private handleCountryByCode(countryCode: string, onDone?: () => void) {
    if (!countryCode) {
      onDone?.();
      return;
    }

    this._listService.searchCountries(countryCode).subscribe({
      next: (response: any) => {
        const countries = response?.countries ?? [];
        const country = countries.find((c: any) =>
          String(c.code) === String(countryCode)
          || String(c.id) === String(countryCode)
          || (c.name && c.name.toUpperCase() === String(countryCode).toUpperCase())
        );

        if (country) {
          this.selectedCountry = {
            id: country.id,
            name: country.name,
            code: country.code,
          };
          this.form.get('obligatedTaxCountryId')?.setValue(country.id, { emitEvent: false });
          this.form.get('obligatedTaxCountry')?.setValue(country.code, { emitEvent: false });
          this.inputCountryValue = country.name;
          this.loadCountryFormats(country.id, onDone);
          return;
        }

        // Si no hay match por búsqueda, al menos mostrar el código guardado
        this.inputCountryValue = String(countryCode);
        onDone?.();
      },
      error: () => {
        this.inputCountryValue = String(countryCode);
        onDone?.();
      }
    });
  }

  toggleCountryValidation(isObligated: any) {
    const countryControl = this.form.get('obligatedTaxCountryId');
    const isObligatedBool = this.isTruthy(isObligated);

    if (isObligatedBool) {
      countryControl?.setValidators([Validators.required]);
    } else {
      countryControl?.clearValidators();
      countryControl?.setValue(null);
      this.form.get('obligatedTaxCountry')?.setValue(null);
      this.inputCountryValue = '';
      this.selectedCountryFormats = [];
      this.filteredCountries = [];
      this.selectedCountry = null;
    }

    countryControl?.updateValueAndValidity();
  }

  toggleTinValidation(isObligated: any) {
    const tinControl = this.form.get('tinNumber');
    const isObligatedBool = this.isTruthy(isObligated);

    if (isObligatedBool) {
      tinControl?.setValidators([Validators.required, this.tinFormatValidator()]);
      if (this.isHydratingForeign) {
        this.applyPendingTin();
      }
    } else if (!this.isHydratingForeign) {
      tinControl?.clearValidators();
      tinControl?.setValue('');
      this.selectedCountryFormats = [];
    } else {
      tinControl?.clearValidators();
    }

    tinControl?.updateValueAndValidity({ emitEvent: false });
  }

  tinFormatValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) return null;

      const countryId = this.form?.get('obligatedTaxCountryId')?.value;
      if (!countryId) return null;

      if (this.selectedCountryFormats.length === 0) {
        return null;
      }

      const tin = control.value;

      const validFormat = this.selectedCountryFormats.some(format => {
        try {
          const regex = new RegExp(format.regular_expression);
          return regex.test(tin);
        } catch (e) {
          return false;
        }
      });

      if (!validFormat) {
        const formatExamples = this.selectedCountryFormats
          .map(f => f.description || f.regular_expression)
          .join(' • ');

        return {
          invalidTinFormat: {
            message: `Formato TIN inválido. Formatos aceptados: ${formatExamples}`
          }
        };
      }

      return null;
    };
  }

  tinValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const isObligated = form.get('isObligatedTaxOtherCountry')?.value;
    const tin = form.get('tinNumber')?.value;

    if (this.isTruthy(isObligated) && !tin) {
      return { tinRequired: true };
    }
    return null;
  }

  updateTinValidation() {
    const tinControl = this.form.get('tinNumber');
    if (tinControl?.hasValidator(Validators.required)) {
      tinControl.updateValueAndValidity();
    }
  }

  togglePublicJobValidation(isPublicJob: any) {
    const positionControl = this.form.get('publicJobPosition');
    const startDateControl = this.form.get('publicJobStartDate');
    const endDateControl = this.form.get('publicJobEndDate');

    if (this.isTruthy(isPublicJob)) {
      positionControl?.setValidators([Validators.required, Validators.minLength(3)]);
      startDateControl?.setValidators([Validators.required, maxTodayColombiaValidator]);
      endDateControl?.setValidators([maxTodayColombiaValidator]);
    } else {
      positionControl?.clearValidators();
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
      positionControl?.setValue(null);
      startDateControl?.setValue(null);
      endDateControl?.setValue(null);
    }

    positionControl?.updateValueAndValidity();
    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  togglePepRelativeValidation(isPepRelative: any) {
    const fullNameControl = this.form.get('pepRelativeFullName');
    const documentTypeControl = this.form.get('pepRelativeDocumentType');
    const documentNumberControl = this.form.get('pepRelativeDocumentNumber');
    const positionEntityControl = this.form.get('pepRelativePositionEntity');

    if (this.isTruthy(isPepRelative)) {
      fullNameControl?.setValidators([Validators.required, Validators.minLength(7)]);
      documentTypeControl?.setValidators([Validators.required]);
      documentNumberControl?.setValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(10),
        Validators.pattern(/^[a-zA-Z0-9]+$/),
      ]);
      positionEntityControl?.setValidators([Validators.required, Validators.minLength(3)]);
    } else {
      fullNameControl?.clearValidators();
      documentTypeControl?.clearValidators();
      documentNumberControl?.clearValidators();
      positionEntityControl?.clearValidators();
      fullNameControl?.setValue(null);
      documentTypeControl?.setValue(null);
      documentNumberControl?.setValue(null);
      positionEntityControl?.setValue(null);
    }

    fullNameControl?.updateValueAndValidity();
    documentTypeControl?.updateValueAndValidity();
    documentNumberControl?.updateValueAndValidity();
    positionEntityControl?.updateValueAndValidity();
  }

  onSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const formValue = this.form.value;
    const tinValue = (formValue.tinNumber ?? this.form.get('tinNumber')?.value ?? '').toString().trim();
    const payload = {
      ...formValue,
      obligatedTaxCountry:
        this.getCountryCode(formValue.obligatedTaxCountryId)
        ?? formValue.obligatedTaxCountry
        ?? this.selectedCountry?.code
        ?? null,
      tin: tinValue || null,
      tinNumber: tinValue || null,
    };

    this._loaderService.show('Enviando código de verificación...');
    this._securityService.setForeign(payload).subscribe({
      next: () => {
        this.sendOtpAndContinue();
      },
      error: (error: any) => {
        this._loaderService.hide();
        console.error("ERROR: ", error);
        toast.error('No pudimos guardar tu información. Intenta de nuevo.');
      }
    });
  }

  private getCountryCode(countryId: number): string | null {
    if (this.selectedCountry && this.selectedCountry.id === countryId) {
      return this.selectedCountry.code;
    }
    const country = this.filteredCountries.find(c => c.id === countryId);
    return country ? country.code : null;
  }

  private sendOtpAndContinue(): void {
    this._securityService.sendOTP().subscribe({
      next: (response: any) => {
        this._loaderService.hide();

        if (response.checkpoint === 'SIGNED_11') {
          this._otpSessionService.clearOtpContext();
          const finishRoute = this._routeService.getNextRoute(this.nextRoute()) ?? '/app/finish';
          localStorage.setItem('route', finishRoute);
          this._router.navigateByUrl(finishRoute);
          return;
        }

        this._otpSessionService.saveOtpContext(response);
        localStorage.setItem('route', this.nextRoute());
        this._router.navigateByUrl(this.nextRoute());
      },
      error: (error: any) => {
        this._loaderService.hide();
        console.error('ERROR sendOTP: ', error);
        toast.error('No pudimos enviar el código de verificación. Verifica tu conexión e intenta de nuevo.');
      }
    });
  }

  handleValue(event: { formName: string, value: boolean | string | number }) {
    // app-selected re-emite el default (false) por debounce y puede pisar getForeign
    if (this.isIgnoringStaleSelected()) {
      const incomingFalse = event.value === false || event.value === 'false';
      const currentTruthy = this.isTruthy(this.form.get(event.formName)?.value);
      if (incomingFalse && currentTruthy) {
        if (event.formName === 'isObligatedTaxOtherCountry') {
          this.applyPendingTin();
        }
        return;
      }
    }

    this.form.get(event.formName)?.setValue(event.value);

    if (event.formName === 'isObligatedTaxOtherCountry') {
      this.toggleCountryValidation(event.value);
      this.toggleTinValidation(event.value);
      if (this.isTruthy(event.value)) {
        this.applyPendingTin();
      } else {
        this.pendingTin = null;
      }
    }
    if (event.formName === 'hasPublicJobManager') {
      this.togglePublicJobValidation(event.value);
    }
    if (event.formName === 'isPepRelative') {
      this.togglePepRelativeValidation(event.value);
    }
  }

  handleInputValue(event: { formName: string, value: string | number }) {
    this.form.get(event.formName)?.setValue(event.value);
    this.form.get(event.formName)?.markAsTouched();
  }

  onTinInput(value: string) {
    // El usuario editó el TIN: ya no forzar el valor hidratado
    this.pendingTin = (value ?? '').toString().trim() || null;
  }

  onSearchChange(value: string) {
    this.inputCountryValue = value;

    if (this.isHydratingForeign) {
      return;
    }

    if (this.selectedCountry && value !== this.selectedCountry.name) {
      this.selectedCountry = null;
      this.form.get('obligatedTaxCountryId')?.setValue(null);
      this.form.get('obligatedTaxCountry')?.setValue(null);
      this.selectedCountryFormats = [];
      this.form.get('tinNumber')?.setValue('');
      this.pendingTin = null;
      this.form.get('obligatedTaxCountryId')?.markAsTouched();
      this.form.updateValueAndValidity();
    }

    this.searchSubject.next(value);
  }

  selectCountry(country: Country) {
    const previousCountryId = this.selectedCountry?.id;
    this.selectedCountry = country;
    this.form.get('obligatedTaxCountryId')?.setValue(country.id);
    this.form.get('obligatedTaxCountry')?.setValue(country.code);
    this.inputCountryValue = country.name;
    this.filteredCountries = [];

    // Solo limpiar TIN si el usuario cambia a otro país (no al re-seleccionar el mismo)
    if (!this.isHydratingForeign && previousCountryId !== country.id) {
      this.form.get('tinNumber')?.setValue('');
      this.pendingTin = null;
    }

    this.form.get('obligatedTaxCountryId')?.markAsTouched();
    this.loadCountryFormats(country.id);

    this.form.updateValueAndValidity();
    this._cdr.detectChanges();
  }

  countryValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const taxObligation = form.get('isObligatedTaxOtherCountry')?.value;
    const countryId = form.get('obligatedTaxCountryId')?.value;

    if (this.isTruthy(taxObligation)) {
      const exists = (this.selectedCountry && this.selectedCountry.id === countryId) ||
                     this.filteredCountries.some(c => c.id === countryId);
      if (!countryId || !exists) {
        return { invalidCountry: true };
      }
    }
    return null;
  }

  private isTruthy(value: any): boolean {
    return value === true || value === 'true';
  }

  private generateExampleFromRegex(regex: string): string {
    try {
      let example = regex
        .replace(/[\[\]\(\)\{\}]/g, '')
        .replace(/\\d/g, '9')
        .replace(/[a-zA-Z]/g, 'X')
        .replace(/\^/g, '')
        .replace(/\$/g, '')
        .replace(/\+/g, '')
        .replace(/\*/g, '')
        .replace(/\?/g, '')
        .replace(/\|/g, '/')
        .replace(/\\/g, '');

      if (example.length > 15) {
        example = example.substring(0, 15);
      }
      return example;
    } catch {
      return regex;
    }
  }

  getTinPlaceholder(): string {
    if (this.isLoadingFormats) {
      return 'Cargando formatos...';
    }

    if (this.selectedCountryFormats.length === 0) {
      return 'Selecciona un país primero';
    }

    const examples = this.selectedCountryFormats
      .map(f => this.generateExampleFromRegex(f.regular_expression))
      .filter(ex => ex && ex.length > 0);

    if (examples.length === 1) {
      return `Ej: ${examples[0]}`;
    } else if (examples.length > 1) {
      return `Ej: ${examples.join(' o ')}`;
    }

    return 'Ingresa tu número TIN';
  }
}
